import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyL, BodyS } from '@sorare/core/src/atoms/typography';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';
import { theme } from '@sorare/core/src/style/theme';

import lineupSet from 'assets/lineup-set.svg';
import { useComposeFlowFF } from 'hooks/compose/useComposeFlowFF';
import { useFootballEvents } from 'lib/events';
import { getAddTeamPath } from 'lib/so5';

import { AddTeamLayout } from '../AddTeamLayout';
import {
  YourNextStepQuery,
  YourNextStepQueryVariables,
} from './__generated__/index.graphql';
import descriptionBg from './assets/description-bg.svg';

const StyledAddTeamLayout = styled(AddTeamLayout)`
  border: 1px solid var(--c-white);
`;
const Wrapper = styled(Vertical)`
  flex: 1;
  margin-bottom: calc(8 * var(--unit));
`;

const GradientText = styled.span`
  background: var(--gradient-bg);
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;

const Description = styled.div`
  background-image: url(${descriptionBg});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  padding: var(--double-unit) 0;
`;

const CTA = styled(LinkOverlay)`
  &,
  &:hover {
    color: var(--c-black);
  }
`;

type Props = {
  so5LeaderboardSlug: Nullable<string>;
};

const ORDERED_RARITIES = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
] as const;

const YOUR_NEXT_STEP_QUERY = gql`
  query YourNextStepQuery($slug: String!) {
    so5 {
      so5Leaderboard(slug: $slug) {
        slug
        rules {
          id
          rarityLimits {
            limited {
              min
              max
            }
            rare {
              min
              max
            }
            superRare {
              min
              max
            }
            unique {
              min
              max
            }
          }
        }
        so5LeaderboardGroup {
          slug
          ...AddTeamLayout_so5LeaderboardGroupInterface
          ...getAddTeamPath_so5LeaderboardGroupInterface
        }
      }
    }
  }

  ${AddTeamLayout.fragments.so5LeaderboardGroupInterface}
  ${getAddTeamPath.fragments.so5LeaderboardGroupInterface}
` as TypedDocumentNode<YourNextStepQuery, YourNextStepQueryVariables>;
export const YourNextStep = ({ so5LeaderboardSlug }: Props) => {
  const track = useFootballEvents();
  const useComposeFlow20 = useComposeFlowFF();
  const { data } = useQuery(YOUR_NEXT_STEP_QUERY, {
    variables: {
      slug: so5LeaderboardSlug || '',
    },
    skip: !so5LeaderboardSlug,
  });
  const { so5Leaderboard } = data?.so5 || {};
  if (!so5Leaderboard) {
    return null;
  }
  const firstRequiredRarity =
    ORDERED_RARITIES.find(
      rarity =>
        (so5Leaderboard.rules?.rarityLimits?.[toCamelCase(rarity)]?.max || 0) >
        0
    ) || Rarity.limited;
  if (!firstRequiredRarity) {
    return null;
  }
  const addTeamPath = getAddTeamPath({
    so5LeaderboardGroupInterface: so5Leaderboard.so5LeaderboardGroup,
    useComposeFlow20,
  });
  return (
    <StyledAddTeamLayout
      so5LeaderboardGroup={so5Leaderboard.so5LeaderboardGroup}
      lineupStyle="spread"
    >
      <Wrapper center gap={4}>
        <Vertical center>
          <img src={lineupSet} alt="" width={40} height={40} />
          <BodyL bold>
            <FormattedMessage
              id="YourNextStep.title"
              defaultMessage="Your Next Step"
            />
          </BodyL>
          {addTeamPath && (
            <CTA
              as={Button}
              color="secondary"
              size="medium"
              to={addTeamPath}
              onClick={() => {
                track('Enter Add Team From Lobby', {
                  source: 'Your Next Step',
                });
              }}
            >
              <FormattedMessage {...glossary.playNow} />
            </CTA>
          )}
        </Vertical>
        <Description>
          <BodyS bold style={{ zIndex: 1 }}>
            <FormattedMessage
              id="YourNextStep.description"
              defaultMessage="Compete with <span>{count, plural, one {# {rarity} Card} other {# {rarity} Cards}}</span>"
              values={{
                span: chunks => (
                  <GradientText
                    style={
                      {
                        '--gradient-bg':
                          theme.colors.scarcity[
                            toCamelCase(firstRequiredRarity)
                          ],
                      } as CSSProperties
                    }
                  >
                    {chunks}
                  </GradientText>
                ),

                count:
                  so5Leaderboard.rules?.rarityLimits?.[
                    toCamelCase(firstRequiredRarity)
                  ].max,
                rarity: (
                  <FormattedMessage
                    {...scarcityMessages[firstRequiredRarity]}
                  />
                ),
              }}
            />
          </BodyS>
        </Description>
      </Wrapper>
    </StyledAddTeamLayout>
  );
};
